import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useHelp } from './HelpContext';
import {
  PRODUCT_TOUR_ENDED,
  PRODUCT_TOUR_STATUS,
  TOUR_SECTIONS,
  farmIdFromPath,
  getTourSteps,
  isTourOfferOpen,
  shouldAutoOfferTour,
} from '../constants/productTour';
import '../components/tour/productTour.css';

const ProductTourContext = createContext({
  phase: 'idle',
  status: null,
  afterSection: false,
  farmId: null,
  takeTour: () => {},
  skipTour: () => {},
  finishTour: () => {},
  openHub: () => {},
  startSection: () => {},
});

function findScrollParent(el) {
  let node = el && el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    const canScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
    if (canScroll && node.scrollHeight > node.clientHeight + 1) return node;
    node = node.parentElement;
  }
  return null;
}

function scrollTargetIntoView(el) {
  if (!el || !(el instanceof Element)) return;
  const scroller = findScrollParent(el);
  if (scroller) {
    const elRect = el.getBoundingClientRect();
    const scRect = scroller.getBoundingClientRect();
    const elCenter = elRect.top + elRect.height / 2;
    const scCenter = scRect.top + scRect.height / 2;
    scroller.scrollTop += elCenter - scCenter;
  } else {
    el.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
  }
}

function waitForSelector(selector, timeoutMs = 12000) {
  return new Promise((resolve) => {
    if (!selector) {
      resolve(true);
      return;
    }
    if (document.querySelector(selector)) {
      resolve(true);
      return;
    }
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      try { observer.disconnect(); } catch (_) { /* already disconnected */ }
      window.clearTimeout(timer);
      resolve(ok);
    };
    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) finish(true);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true });
    const timer = window.setTimeout(() => finish(!!document.querySelector(selector)), timeoutMs);
  });
}

function wait(ms) {
  return new Promise((resolve) => { window.setTimeout(resolve, ms); });
}

function isPresent(selector) {
  const el = typeof selector === 'string' ? document.querySelector(selector) : selector;
  if (!el || !(el instanceof Element)) return false;
  const rect = el.getBoundingClientRect();
  return el.getClientRects().length > 0 && (rect.width > 0 || rect.height > 0);
}

async function waitForPresent(selector, timeoutMs = 12000) {
  if (!selector) return true;
  const deadline = Date.now() + timeoutMs;
  if (isPresent(selector)) return true;
  await waitForSelector(selector, Math.max(0, deadline - Date.now()));
  while (Date.now() < deadline) {
    if (isPresent(selector)) return true;
    await wait(40);
  }
  return isPresent(selector);
}

function toDriverSteps(defs, { openHelp, lockHelp }) {
  return defs.map((def, index) => {
    const popover = {
      title: def.title,
      description: def.description,
      side: def.side || 'bottom',
      align: def.align || 'start',
    };
    if (index === 0) {
      popover.nextBtnText = 'Proceed';
      popover.doneBtnText = 'Proceed';
    }
    if (def.openHelpOnNext) {
      popover.onNextClick = (_el, _step, { driver: d }) => {
        try {
          openHelp();
          lockHelp();
        } catch (err) {
          console.error('Product tour: could not open Help', err);
        }
        d.moveNext();
      };
    }
    return {
      element: def.element,
      waitForElement: def.waitForElement != null ? def.waitForElement : 0,
      skipMissingElement: true,
      popover,
    };
  });
}

export function ProductTourProvider({ children, farmIds = [] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { openHelp, closeHelp, lockHelp, unlockHelp } = useHelp();

  const [status, setStatus] = useState(null);
  const [statusReady, setStatusReady] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [afterSection, setAfterSection] = useState(false);

  const driverRef = useRef(null);
  const ignoreDestroyRef = useRef(false);
  const launchTimerRef = useRef(null);
  const scrollUnsubRef = useRef(null);
  const tourGenRef = useRef(0);
  const farmIdsRef = useRef(farmIds);
  const statusRef = useRef(status);
  const openHelpRef = useRef(openHelp);
  const lockHelpRef = useRef(lockHelp);
  const unlockHelpRef = useRef(unlockHelp);
  const closeHelpRef = useRef(closeHelp);

  useEffect(() => { farmIdsRef.current = farmIds; }, [farmIds]);
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { openHelpRef.current = openHelp; }, [openHelp]);
  useEffect(() => { lockHelpRef.current = lockHelp; }, [lockHelp]);
  useEffect(() => { unlockHelpRef.current = unlockHelp; }, [unlockHelp]);
  useEffect(() => { closeHelpRef.current = closeHelp; }, [closeHelp]);

  const farmId = farmIdFromPath(location.pathname) || farmIds[0] || null;

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return undefined;
    const unsub = onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const next = snap.exists() ? snap.data().productTourStatus : null;
        setStatus(next || null);
        setStatusReady(true);
      },
      (err) => {
        console.error('Product tour status listener failed:', err?.message || err);
        setStatusReady(true);
      }
    );
    return () => unsub();
  }, []);

  // Offer the welcome once to anyone who has never taken or dismissed the tour
  // (missing status counts as never offered, including existing accounts).
  // After skip or complete, replay only from Help or the account menu.
  useEffect(() => {
    if (!statusReady) return;
    if (!isTourOfferOpen(status)) return;
    if (phase !== 'idle') return;
    if (!shouldAutoOfferTour(location.pathname)) return;
    setPhase('welcome');
  }, [statusReady, status, phase, location.pathname]);

  const persistStatus = useCallback(async (next) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    try {
      await updateDoc(doc(db, 'users', uid), { productTourStatus: next });
      setStatus(next);
      statusRef.current = next;
    } catch (err) {
      console.error('productTourStatus save failed', err);
    }
  }, []);

  const destroyQuiet = useCallback(() => {
    ignoreDestroyRef.current = true;
    tourGenRef.current += 1;
    if (launchTimerRef.current) {
      window.clearTimeout(launchTimerRef.current);
      launchTimerRef.current = null;
    }
    if (scrollUnsubRef.current) {
      try { scrollUnsubRef.current(); } catch (_) { /* already gone */ }
      scrollUnsubRef.current = null;
    }
    try {
      driverRef.current?.destroy();
    } catch (_) { /* already gone */ }
    driverRef.current = null;
    ignoreDestroyRef.current = false;
  }, []);

  const skipTour = useCallback(async () => {
    destroyQuiet();
    try {
      unlockHelpRef.current?.();
      closeHelpRef.current?.();
    } catch (_) { /* help not mounted */ }
    setAfterSection(false);
    setPhase('idle');
    if (isTourOfferOpen(statusRef.current)) {
      await persistStatus(PRODUCT_TOUR_STATUS.SKIPPED);
    }
  }, [destroyQuiet, persistStatus]);

  const finishTour = useCallback(async () => {
    destroyQuiet();
    try {
      unlockHelpRef.current?.();
      closeHelpRef.current?.();
    } catch (_) { /* help not mounted */ }
    setAfterSection(false);
    setPhase('idle');
    await persistStatus(PRODUCT_TOUR_STATUS.COMPLETED);
  }, [destroyQuiet, persistStatus]);

  const takeTour = useCallback(() => {
    setAfterSection(false);
    setPhase('hub');
    if (isTourOfferOpen(statusRef.current)) {
      persistStatus(PRODUCT_TOUR_STATUS.SKIPPED);
    }
  }, [persistStatus]);

  const openHub = useCallback(() => {
    destroyQuiet();
    try {
      unlockHelpRef.current?.();
      closeHelpRef.current?.();
    } catch (_) { /* help not mounted */ }
    setAfterSection(false);
    setPhase('hub');
  }, [destroyQuiet]);

  const startSection = useCallback((sectionId) => {
    const section = TOUR_SECTIONS.find((s) => s.id === sectionId);
    if (!section) return;

    const ids = farmIdsRef.current || [];
    const resolvedFarm = farmIdFromPath(window.location.pathname) || ids[0] || null;
    if (section.needsProperty && !resolvedFarm) return;

    destroyQuiet();
    const tourGen = ++tourGenRef.current;
    setPhase('driving');

    const targetPath = typeof section.path === 'function' ? section.path(resolvedFarm) : null;
    if (targetPath && window.location.pathname !== targetPath) {
      navigate(targetPath);
    }

    const defs = getTourSteps(sectionId);
    if (!defs.length) {
      setAfterSection(true);
      setPhase('hub');
      return;
    }

    const attachScrollRefresh = (el, instance) => {
      if (scrollUnsubRef.current) {
        try { scrollUnsubRef.current(); } catch (_) { /* replace */ }
        scrollUnsubRef.current = null;
      }
      if (!el || !(el instanceof Element)) return;
      const scroller = findScrollParent(el);
      if (!scroller) return;
      const onScroll = () => {
        try { instance.refresh(); } catch (_) { /* destroyed */ }
      };
      scroller.addEventListener('scroll', onScroll, { passive: true });
      scrollUnsubRef.current = () => scroller.removeEventListener('scroll', onScroll);
    };

    const launch = async () => {
      if (targetPath) {
        const pathDeadline = Date.now() + 5000;
        while (window.location.pathname !== targetPath && Date.now() < pathDeadline) {
          await wait(40);
        }
      }
      if (tourGen !== tourGenRef.current) return;
      if (section.readySelector) {
        await waitForPresent(section.readySelector, 12000);
      }
      if (tourGen !== tourGenRef.current) return;
      if (section.prepareEvent) {
        window.dispatchEvent(new CustomEvent(section.prepareEvent));
        await wait(80);
        const selectors = section.prepareSelectors || [];
        await Promise.all(selectors.map((sel) => waitForPresent(sel, 12000)));
      }
      if (tourGen !== tourGenRef.current) return;
      if (sectionId === 'dashboard') {
        await waitForSelector('[data-tour="dash-properties"]', 4000);
      }
      await wait(120);
      if (tourGen !== tourGenRef.current) return;

      const visibleDefs = defs.filter((def) => {
        if (def.optional) return isPresent(def.element);
        if (def.waitForElement) return true;
        return isPresent(def.element);
      });
      if (!visibleDefs.length) {
        setAfterSection(true);
        setPhase('hub');
        return;
      }

      const steps = toDriverSteps(visibleDefs, {
        openHelp: () => openHelpRef.current?.(),
        lockHelp: () => lockHelpRef.current?.(),
      });

      const instance = driver({
        overlayColor: '#043132',
        overlayOpacity: 0.55,
        stagePadding: 8,
        stageRadius: 12,
        popoverOffset: 12,
        showProgress: true,
        progressText: '{{current}} of {{total}}',
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        doneBtnText: 'Done',
        popoverClass: 'ei-tour-popover',
        animate: true,
        duration: 200,
        smoothScroll: false,
        allowClose: true,
        overlayClickBehavior: 'close',
        skipMissingElement: true,
        waitForElement: 0,
        disableActiveInteraction: true,
        onHighlightStarted: (el, _step, { driver: d }) => {
          scrollTargetIntoView(el);
          attachScrollRefresh(el, d);
        },
        onHighlighted: (el, _step, { driver: d }) => {
          scrollTargetIntoView(el);
          attachScrollRefresh(el, d);
          window.requestAnimationFrame(() => {
            try { d.refresh(); } catch (_) { /* destroyed */ }
            window.setTimeout(() => {
              try { d.refresh(); } catch (_) { /* destroyed */ }
            }, 120);
          });
        },
        onNextClick: (_el, _step, { driver: d }) => {
          if (d.isLastStep()) {
            d.destroy();
            return;
          }
          d.moveNext();
        },
        onDestroyed: () => {
          if (scrollUnsubRef.current) {
            try { scrollUnsubRef.current(); } catch (_) { /* already gone */ }
            scrollUnsubRef.current = null;
          }
          try {
            unlockHelpRef.current?.();
            closeHelpRef.current?.();
          } catch (_) { /* help not mounted */ }
          if (ignoreDestroyRef.current) return;
          try { window.dispatchEvent(new Event(PRODUCT_TOUR_ENDED)); } catch (_) { /* ignore */ }
          driverRef.current = null;
          setAfterSection(true);
          setPhase('hub');
        },
      });

      instance.setSteps(steps);
      if (tourGen !== tourGenRef.current) {
        try { instance.destroy(); } catch (_) { /* superseded */ }
        return;
      }
      driverRef.current = instance;
      instance.drive();
    };

    launch().catch((err) => {
      console.error('Product tour failed to start', err);
      setAfterSection(true);
      setPhase('hub');
    });
  }, [destroyQuiet, navigate]);

  useEffect(() => () => {
    ignoreDestroyRef.current = true;
    try { driverRef.current?.destroy(); } catch (_) { /* unmount */ }
    driverRef.current = null;
  }, []);

  const value = useMemo(() => ({
    phase,
    status,
    afterSection,
    farmId,
    takeTour,
    skipTour,
    finishTour,
    openHub,
    startSection,
  }), [phase, status, afterSection, farmId, takeTour, skipTour, finishTour, openHub, startSection]);

  return (
    <ProductTourContext.Provider value={value}>
      {children}
    </ProductTourContext.Provider>
  );
}

export function useProductTour() {
  return useContext(ProductTourContext);
}
